import React, { useEffect, useRef, useState } from 'react';
import { Grid, Dialog, Toolbar, Typography, DialogContent, Button, DialogActions } from '@mui/material';
import Moment from 'react-moment';
import Status from '../../components/status';
import EnhancedTable from '../../components/table';
import Config from '../../config';
import { Link } from 'react-router-dom';
import { getCompanyNames } from '../../helpers';
import DownloadButton from '../../components/download_button';
import axios from 'axios';

interface TransferTableProps {
    isRetailer?: boolean;
    filter?: any;
    filterInUrl?: boolean;
    title?: string;
    [key: string]: any;
}

interface TableUrlBuilder {
    filter?: string;
    orderBy?: string;
}

interface TransferTableState {
    showError: boolean;
    showBulkRetry: boolean;
    error?: string;
    disableRetry?: boolean;
    refresh?: boolean;
    tableUrlBuilder?: TableUrlBuilder;
    selectedFailedPayouts?: number;
}

const TransferTable = (props: TransferTableProps) => {
    const [state, setState] = useState<TransferTableState>({showError: false, showBulkRetry: false});
    const selected = useRef<any[]>([]);

    const buildExport = () => {
        let type = 'xlsx';
        return {
            href: Config.api + `/odata/Company/Functions.ExportPaymentRecords?fileType=${type}` + (state.tableUrlBuilder.filter || '') + (state.tableUrlBuilder.orderBy || ''),
            fileName: "paymentrecords-" + Math.floor(Date.now() / 1000).toString() + '.' + type
    };
    }
    const onError = (error: string) => {
        setState(prev => ({...prev, showError: true, error: error}));
    }
    const showBulkRetry = () => {
        setState(prev => ({...prev, showBulkRetry: true, disableRetry: false}));
    }
    const retry = async () => {
        setState(prev => ({...prev, disableRetry: true}));
        var toRetry = selected.current.filter(r => r.Status === 'Failed' && r.PaymentType === 'Payout').map(r => r.Id);
        try{
            let model = {Ids: toRetry};
            await axios.post(Config.api + `/odata/Company/Functions.RetryPayments`, model);
            setState(prev => ({...prev, showBulkRetry: false, refresh: !prev.refresh}));
        }catch{
            setState(prev => ({...prev, showError: true, error: 'Failed to retry payments.', refresh: !prev.refresh}));
        }
    }

    let ledgerConfig = {
        url: Config.api + '/odata/Company/PaymentRecords',
        columns: [
          { id: "Id", label: 'ID', width: '10em', toggleable: true},
          { id: 'Partner', label: 'Partner', filterable: true, getOptions: getCompanyNames, toggleable: true},
          { id: 'OrderId', type: 'number', filterable: true, label: 'Order', width: '10em', template: (v: any) => <Link to={'/order-management/order-details?orderid=' + v}>{v}</Link>, toggleable: true },
          { id: 'MarketplaceOrderId', filterable: true, label: 'Marketplace Order', width: '20em', template: (v: any) => <Link to={'/search?term=' + v}>{v}</Link>, toggleable: true },
          { id: 'ExternalId', label: 'External ID', width: '15em', toggleable: true, hidden: true, filterable: true },
          { id: 'PayoutId', label: 'Payout ID', width: '15em', toggleable: true, hidden: true, filterable: true },
          { id: 'Line', label: 'Line', width: '10em', toggleable: true },
          { id: 'PaymentType', label: 'Type', width: '10em', toggleable: true, filterable: true },
          { id: 'TransferAmount', label: 'Amount', width: '10em', template: (value: any) => value != null ? value.toFixed(2) : value, toggleable: true },
          { id: 'Status', label: 'Status', filterable: true, template: (value: any) => <Status description={value}/>, toggleable: true},
          { id: 'Created', type:'date', toggleable: true, filterable: true, sortable: true, label: 'Created', template: (value: any) => (<Moment format='MM/DD/YYYY hh:mm A'>{value}</Moment>), width: '15em' },
          { command: 'commands', stopPropagation: 'true', width: '8rem', template: (value: any, row: any) => (
              <>
                <Button size='small' variant='outlined' component={Link} to={`/payments/transfer?id=${row.Id}`}>View</Button>
             </>
          )}
      ],
      actions: <div className="tableActions">
        <DownloadButton onError={onError} disabled={!state.tableUrlBuilder} size='small' variant='outlined' onClick={buildExport}>Export</DownloadButton>
      </div>,
      selectable: props.isRetailer,
      filter: props.filter,
      filterInUrl: props.filterInUrl,
      order: 'desc',
      orderBy: 'Id',
      keyField: 'Id',
      pageSize: 20,
      refresh: state.refresh,
      pageSizes: [10, 20, 50],
      onGetData: (builder: TableUrlBuilder) => setState(prev => ({...prev, tableUrlBuilder: builder})),
      title: props.title || 'Transfer Records',
      setSelected: (ids: any[], models: any[]) => {
          selected.current = models;
          let selectedFailedPayouts = models.filter(r => r.Status === 'Failed' && r.PaymentType === 'Payout').length;
          setState(prev => prev.selectedFailedPayouts !== selectedFailedPayouts ? {...prev, selectedFailedPayouts} : prev);
      },
      selectedActions: <div style={{display: 'flex'}}>
        {props.isRetailer && <Button disabled={!state.selectedFailedPayouts} onClick={showBulkRetry}>Retry</Button>}
        </div>,
  };
  return <>
    <EnhancedTable config={ledgerConfig}/>
    <Dialog open={state.showError} onClose={() => {setState(prev => ({...prev, showError: false}))}}>
        <Toolbar className='lbtoolbar'>{'Error'}</Toolbar>
        <DialogContent><Typography>{state.error}</Typography></DialogContent>
    </Dialog>
    <Dialog maxWidth="sm" fullWidth open={state.showBulkRetry} onClose={() => {setState(prev => ({...prev, showBulkRetry: false}))}}>
        <Toolbar className='lbtoolbar'>{'Retry'}</Toolbar>
        <DialogContent>
            <Typography>
            {state.selectedFailedPayouts === 1 ? 'Are you sure you want to retry this failed transfer?' : `Are you sure you want to retry these ${state.selectedFailedPayouts} failed transfers?`}
            </Typography>
        </DialogContent>
        <DialogActions>
        <DialogActions>
          <Button variant='text' disabled={state.disableRetry} onClick={() => setState(prev => ({...prev, showBulkRetry: false}))} color="primary">
            No
          </Button>
          <Button variant='text' disabled={state.disableRetry} onClick={retry} color="primary">
            Yes
          </Button>
        </DialogActions>
        </DialogActions>
    </Dialog>
    </>;
}

interface PaymentTransfersProps {
    setTitle?: (title: string) => void;
    isRetailer?: boolean;
    [key: string]: any;
}

const PaymentTransfers = (props: PaymentTransfersProps) => {
    useEffect(() => {
        if (props.setTitle) {
            props.setTitle('Payment Center');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <Grid container spacing={2}>
        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
        <TransferTable isRetailer={props.isRetailer} filterInUrl={true}/>
    </Grid>
    </Grid>;
}

export default PaymentTransfers;
export {TransferTable};
